import React, { useState, useRef, useEffect } from 'react';

// 目標日（達成予定日）の計算
// 目標日 = 生年月日 + (年×365.25 + 月×30.4375) 日
export function calcTargetDate(birthdate, targetYears, targetMonths) {
  if (!birthdate) return null;
  const parts = birthdate.split('-');
  if (parts.length !== 3) return null;
  const birthMs = new Date(
    parseInt(parts[0], 10),
    parseInt(parts[1], 10) - 1,
    parseInt(parts[2], 10)
  ).getTime();
  if (isNaN(birthMs)) return null;
  const offsetDays =
    (Number(targetYears) || 0) * 365.25 + (Number(targetMonths) || 0) * 30.4375;
  const target = new Date(birthMs + offsetDays * 86400000);
  return isNaN(target.getTime()) ? null : target;
}

// 残り日数の計算
// 残り日数 = 目標日 - new Date()
export function calcRemainingDays(birthdate, targetYears, targetMonths, now) {
  const target = calcTargetDate(birthdate, targetYears, targetMonths);
  if (!target) return null;
  const nowMs = (now || new Date()).getTime();
  const diffDays = Math.ceil((target.getTime() - nowMs) / 86400000);
  return diffDays;
}

// 達成年月日の表示形式は達成済みモーダルと同じ YYYY/MM/DD に揃える
function formatTargetDate(date) {
  if (!date) return '';
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
}

function formatRemaining(days) {
  if (days === null || days === undefined) return '生年月日未設定';
  if (days > 0) return `あと ${days.toLocaleString()}日`;
  if (days === 0) return '今日が目標日';
  return `${Math.abs(days).toLocaleString()}日 経過`;
}

// 達成ボタンは誤タップ防止のため 1 秒長押しに変更（TaskItem の 0.5 秒長押しと同様の Pointer Events 方式）
const LONG_PRESS_MS = 1000;
const LONG_PRESS_MOVE_TOLERANCE = 10;

export default function BucketListItem({ item, birthdate, now, onComplete }) {
  const targetDate = calcTargetDate(birthdate, item.targetYears, item.targetMonths);
  const targetDateText = formatTargetDate(targetDate);
  const days = calcRemainingDays(birthdate, item.targetYears, item.targetMonths, now);

  const daysClass =
    days === null
      ? 'bucket-days-unset'
      : days < 0
      ? 'bucket-days-passed'
      : days <= 30
      ? 'bucket-days-soon'
      : 'bucket-days-normal';

  const [isPressing, setIsPressing] = useState(false);
  const pressTimerRef = useRef(null);
  const pressStartRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    };
  }, []);

  const cancelLongPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    pressStartRef.current = null;
    setIsPressing(false);
  };

  const handlePressStart = (e) => {
    // v1.7.2: 長押し中のテキスト範囲選択（PC：青ハイライト／iOS：拡大鏡＋コンテキストメニュー）を抑制
    if (e.preventDefault) e.preventDefault();
    pressStartRef.current = { x: e.clientX, y: e.clientY };
    setIsPressing(true);
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      pressStartRef.current = null;
      setIsPressing(false);
      if (navigator.vibrate) navigator.vibrate(60);
      onComplete(item.id);
    }, LONG_PRESS_MS);
  };

  const handlePressMove = (e) => {
    if (!pressStartRef.current) return;
    const dx = Math.abs(e.clientX - pressStartRef.current.x);
    const dy = Math.abs(e.clientY - pressStartRef.current.y);
    if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
      cancelLongPress();
    }
  };

  return (
    <div className="bucket-item">
      <button
        type="button"
        className={`bucket-check-btn${isPressing ? ' is-pressing' : ''}`}
        onPointerDown={handlePressStart}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onPointerCancel={cancelLongPress}
        onPointerMove={handlePressMove}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={`「${item.name}」を達成済みにする（1秒長押し）`}
        title="1秒長押しで達成"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span className="bucket-check-progress" aria-hidden="true" />
      </button>
      <span className="bucket-item-name">
        {item.name}
        <span className="bucket-press-hint">長押しで達成</span>
      </span>
      <span className="bucket-item-meta">
        <span className={`bucket-item-days ${daysClass}`}>{formatRemaining(days)}</span>
        {targetDateText && (
          <span className="bucket-item-target-date" title={`達成予定日 ${targetDateText}`}>
            {targetDateText}
          </span>
        )}
      </span>
    </div>
  );
}
